import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSessionContext } from '@supabase/auth-helpers-react';
import { isAdminEmail } from '../lib/adminAllowlist';
import { WEB_RECOVERY_PATH } from '../lib/nativeRecoveryContinuation';
import { wasRecoveryRouteLoad } from '../lib/recoveryHandoff';

export type LoginResult = 'success' | 'invalid' | 'denied';

interface AuthContextType {
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { session, isLoading: sessionLoading, supabaseClient } = useSessionContext();
  const [authLoading, setAuthLoading] = useState(false);

  // Ensure any session tied to an email outside the admin allowlist is cleared.
  useEffect(() => {
    if (sessionLoading) return;

    // The ephemeral reset-route client must keep a rider's recovery session
    // for the proof-bound password update — signing it out destroys the
    // validated flow. Only on this exact route's bootstrap load; the guard
    // still runs on every other path and Admin authorization is unchanged.
    const onRecoveryRouteLoad =
      wasRecoveryRouteLoad() &&
      typeof window !== 'undefined' &&
      window.location.pathname === WEB_RECOVERY_PATH;

    if (session?.user && !isAdminEmail(session.user.email) && !onRecoveryRouteLoad) {
      supabaseClient.auth.signOut().catch(error => {
        console.error('Failed to sign out unauthorized session:', error);
      });
    }
  }, [session, sessionLoading, supabaseClient]);

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    // Note: Do NOT set authLoading here. The Login component manages its own
    // loading state. Setting authLoading causes ProtectedRoute to unmount Login
    // (replacing it with a spinner), which loses any error messages when login fails.
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const { data, error } = await supabaseClient.auth.signInWithPassword({
        email: normalizedEmail,
        password,
      });

      if (error) {
        console.error('Supabase login error:', error);
        return 'invalid';
      }

      const signedInEmail = data.session?.user?.email;
      if (!isAdminEmail(signedInEmail)) {
        console.warn('Login blocked: email is not in the admin allowlist', signedInEmail);
        await supabaseClient.auth.signOut();
        return 'denied';
      }

      return 'success';
    } catch (error) {
      console.error('Login failed:', error);
      return 'invalid';
    }
  }, [supabaseClient]);

  const logout = useCallback(async () => {
    setAuthLoading(true);
    try {
      await supabaseClient.auth.signOut();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setAuthLoading(false);
    }
  }, [supabaseClient]);

  const loading = sessionLoading || authLoading;
  const isAuthenticated = !!session?.user && isAdminEmail(session.user.email);

  const value = useMemo(() => ({
    isAuthenticated,
    loading,
    login,
    logout,
  }), [isAuthenticated, loading, login, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

